// A plan before you start it: its cover standing beside its title, what it is, its days, and how to
// begin (alone or together, today or next Monday; a calendar plan by date or from its first day).
// Together: choose friends now, or share the invite link once it starts.
import { createFileRoute, useNavigate, useRouter } from "@tanstack/react-router";
import { useId, useState } from "react";
import { Avatar } from "../../../components/Avatar";
import { Button, IconButton } from "../../../components/Button";
import { Sheet } from "../../../components/Dialog";
import { EmptyState } from "../../../components/Empty";
import { PlanCover } from "../../../components/PlanCover";
import { Skeleton } from "../../../components/Skeleton";
import { SegmentedControl } from "../../../components/Tabs";
import ui from "../../../components/ui.module.css";
import { useJourneys, usePlan, useStartPlan } from "../../../data/planQueries";
import { addDays, appointedOrdinal, atNoon, finishDate, localDate, planLine, type FollowMode, type Plan } from "../../../data/plans";
import { useFriends } from "../../../data/queries";
import { useTitle } from "../../../lib/useTitle";
import { CompanionLine } from "./-readings";
import s from "./about.module.css";

export const Route = createFileRoute("/_app/plans/about/$planId")({ component: About });

const nextMonday = (today: string) => addDays(today, ((8 - atNoon(today).getUTCDay()) % 7) || 7);
const long = (iso: string) => atNoon(iso).toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" });

function About() {
  const { planId } = Route.useParams();
  const { data: plan, isPending } = usePlan(planId);
  const { data: journeys = [] } = useJourneys();
  const router = useRouter();
  const navigate = useNavigate();
  const [starting, setStarting] = useState(false);
  const [together, setTogether] = useState(false);
  const [all, setAll] = useState(false);
  useTitle(plan?.title ?? "Plan");

  if (isPending)
    return (
      <div className={ui.page} aria-busy="true">
        <div className={s.head}>
          <Skeleton width={128} height={205} shape="block" />
          <Skeleton width="70%" height={40} />
        </div>
      </div>
    );
  if (!plan)
    return (
      <div className={ui.page}>
        <EmptyState title="This plan isn’t available." body="It may have been withdrawn by the friend who made it." />
      </div>
    );

  // A calendar plan opens at today's date; a sequence at its first day.
  const from = plan.scheduleKind === "calendar" ? appointedOrdinal(plan.schedule.map((d) => d.key), localDate()) - 1 : 0;
  const shown = all ? plan.schedule : plan.schedule.slice(from, from + 7);
  const reading = journeys.find((j) => j.plan.id === plan.id);
  const begin = (t: boolean) => {
    setTogether(t);
    setStarting(true);
  };

  return (
    <div className={ui.page}>
      <div className={ui.pageHead}>
        <IconButton label="Back" icon="back" className={s.back} onClick={() => router.history.back()} />
      </div>

      <header className={s.head}>
        <span className={s.cover}>
          <PlanCover plan={plan} width={132} />
        </span>
        <div className={s.headText}>
          <h1 className={s.title}>{plan.title}</h1>
          <p className={s.meta}>
            {plan.author && <Avatar user={plan.author} size={22} />}
            <span>
              {planLine(plan)}.{plan.classic ? " From Bible Standard’s library of plans." : ""}
            </span>
          </p>
        </div>
      </header>

      {plan.description && <p className={s.description}>{plan.description}</p>}
      {plan.companionWork ? (
        <CompanionLine work={plan.companionWork} className={s.companion} />
      ) : (
        plan.companion && (
          <p className={s.companion}>
            Read beside <cite>{plan.companion}</cite>
          </p>
        )
      )}

      <div className={s.start}>
        {reading && (
          <Button variant="primary" onClick={() => void navigate({ to: "/plans/$journeyId", params: { journeyId: reading.id } })}>
            Continue reading
          </Button>
        )}
        <Button variant={reading ? "secondary" : "primary"} icon="people" onClick={() => begin(true)}>
          Read with friends
        </Button>
        <Button variant="secondary" onClick={() => begin(false)}>
          Read alone
        </Button>
      </div>
      <p className={s.pace}>Everyone reads at their own pace. Friends see who has read each day, never who is ahead.</p>

      <section className={s.schedule} aria-labelledby="sched-head">
        <h2 id="sched-head" className={s.scheduleTitle}>
          {plan.scheduleKind === "calendar" && !all ? "The days from today" : "The days"}
        </h2>
        <ol>
          {shown.map((d) => (
            <li key={d.key}>
              <span className={s.day}>{d.heading}</span>
              <span className={s.readings}>
                {d.readings.map((r, i) => (
                  <span key={r.key} className={r.kind === "section" ? s.section : undefined}>
                    {r.label}
                    {i < d.readings.length - 1 ? ", " : ""}
                  </span>
                ))}
              </span>
            </li>
          ))}
        </ol>
        {plan.schedule.length > 7 && (
          <Button variant="text" size="sm" iconEnd={all ? undefined : "forward"} onClick={() => setAll(!all)}>
            {all ? "Show fewer days" : `Show all ${plan.days} days`}
          </Button>
        )}
      </section>

      <StartSheet plan={plan} together={together} open={starting} onClose={() => setStarting(false)} />
    </div>
  );
}

function StartSheet({ plan, together, open, onClose }: { plan: Plan; together: boolean; open: boolean; onClose: () => void }) {
  const { data: friends } = useFriends();
  const start = useStartPlan();
  const navigate = useNavigate();
  const formId = useId();
  const [when, setWhen] = useState<"today" | "monday">("today");
  const calendar = plan.scheduleKind === "calendar";
  const [follow, setFollow] = useState<FollowMode>(calendar ? "byDate" : "fromDayOne");
  const [chosen, setChosen] = useState<string[]>([]);

  const today = localDate();
  const startOn = when === "today" ? today : nextMonday(today);
  const keys = plan.schedule.map((d) => d.key);
  const firstHeading = follow === "byDate" ? plan.schedule[appointedOrdinal(keys, startOn) - 1]?.heading : plan.schedule[0]?.heading;

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title={together ? "Read together" : "Read alone"}
      actions={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" form={formId} busy={start.isPending} disabled={start.isPending}>
            {together ? (chosen.length ? `Start and invite ${chosen.length}` : "Start together") : "Start"}
          </Button>
        </>
      }
    >
      <form
        id={formId}
        className={s.sheetForm}
        onSubmit={(e) => {
          e.preventDefault();
          start.mutate(
            { planId: plan.id, together, invite: chosen, startOn, ...(calendar ? { follow } : {}) },
            { onSuccess: (j) => void navigate({ to: "/plans/$journeyId", params: { journeyId: j.id } }) },
          );
        }}
      >
        <SegmentedControl
          label="Start"
          wide
          options={[
            { value: "today", label: "Start today" },
            { value: "monday", label: "Next Monday" },
          ]}
          value={when}
          onChange={setWhen}
        />
        {calendar && (
          <SegmentedControl
            label="Follow"
            wide
            options={[
              { value: "byDate", label: "By date" },
              { value: "fromDayOne", label: `From ${plan.schedule[0]?.heading ?? "day 1"}` },
            ]}
            value={follow}
            onChange={setFollow}
          />
        )}
        <p className={s.small}>
          {follow === "byDate"
            ? `You’ll read each date’s readings on that date, beginning with ${firstHeading} on ${long(startOn)}. Nothing earlier in the year counts as behind.`
            : calendar
              ? `You’ll begin with ${firstHeading}’s readings on ${long(startOn)} and go on a day at a time; the last day is ${long(finishDate(startOn, plan))}.`
              : `Day 1 is ${long(startOn)}; the last day is ${long(finishDate(startOn, plan))}.`}
        </p>

        {together && (
          <fieldset className={s.friends}>
            <legend className={s.legend}>Invite friends</legend>
            {friends?.friends.map((f) => (
              <label key={f.id} className={s.friend}>
                <Avatar user={f} size={32} />
                <span className={s.friendName}>{f.displayName}</span>
                <input
                  type="checkbox"
                  className={s.check}
                  checked={chosen.includes(f.id)}
                  onChange={(e) => setChosen(e.target.checked ? [...chosen, f.id] : chosen.filter((x) => x !== f.id))}
                />
              </label>
            ))}
            <p className={s.small}>You can also share an invite link once the plan starts, for people who aren’t your friends yet.</p>
          </fieldset>
        )}
      </form>
    </Sheet>
  );
}
